import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Check,
  X,
  AlertCircle,
  Building2,
  Package,
  TrendingUp,
  Edit,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";

export interface VendorAnalysis {
  transactionIndex: number;
  description: string;
  amount: number;
  date: string;
  llmExtraction: {
    vendor: {
      name: string;
      category: string;
      subcategory?: string;
      swissVendor: boolean;
      uid?: string;
    };
    product: {
      name: string;
      type: string;
      category: string;
    };
    mwst: {
      applicable: boolean;
      rate: string | number; // Can be "0" | "2.5" | "3.7" | "7.7" string or number
      category: string;
      reasoning: string;
    };
    confidence: number;
    reasoning: string;
  };
  enrichment: {
    uid: string;
    name: string;
    legalForm?: string;
    mwstRegistered?: boolean;
    mwstNumber?: string;
  } | null;
  recommendation: {
    createVendor: boolean;
    vendorData: any;
    productData: any;
  };
}

interface SmartAnalysisModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  analyses: VendorAnalysis[];
  onApprove: (selectedIndices: number[]) => void;
  isProcessing: boolean;
  onCorrect?: (analysis: VendorAnalysis) => void;
}

export function SmartAnalysisModal({
  open,
  onOpenChange,
  analyses,
  onApprove,
  isProcessing,
  onCorrect,
}: SmartAnalysisModalProps) {
  const [selectedVendors, setSelectedVendors] = useState<Set<number>>(
    new Set(
      analyses
        .filter(a => a.recommendation.createVendor)
        .map(a => a.transactionIndex)
    )
  );

  const toggleVendor = (index: number) => {
    const newSelected = new Set(selectedVendors);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedVendors(newSelected);
  };

  const toggleAll = () => {
    if (selectedVendors.size === analyses.length) {
      setSelectedVendors(new Set());
    } else {
      setSelectedVendors(new Set(analyses.map(a => a.transactionIndex)));
    }
  };

  const handleApprove = () => {
    onApprove(Array.from(selectedVendors));
  };

  const getConfidenceBadge = (confidence: number) => {
    if (confidence >= 80) {
      return (
        <Badge className="bg-[var(--success)] hover:bg-[var(--success)]/80">
          <Check className="h-3 w-3 mr-1" />
          {confidence}% High
        </Badge>
      );
    } else if (confidence >= 60) {
      return (
        <Badge className="bg-[var(--warning)] hover:bg-[var(--warning)]/80">
          <AlertCircle className="h-3 w-3 mr-1" />
          {confidence}% Medium
        </Badge>
      );
    } else {
      return (
        <Badge variant="destructive">
          <X className="h-3 w-3 mr-1" />
          {confidence}% Low
        </Badge>
      );
    }
  };

  const getMWSTBadge = (rate: string | number) => {
    const numericRate = typeof rate === "string" ? parseFloat(rate) : rate;
    const colors: Record<number, string> = {
      0: "bg-muted",
      2.5: "bg-[var(--info)]",
      3.7: "bg-primary",
      7.7: "bg-primary",
    };
    return (
      <Badge
        className={`${colors[numericRate] || "bg-muted"} hover:${colors[numericRate] || "bg-muted"}/80`}
      >
        {rate}% MWST
      </Badge>
    );
  };

  const highConfidence = analyses.filter(
    a => a.llmExtraction.confidence >= 80
  ).length;
  const mediumConfidence = analyses.filter(
    a => a.llmExtraction.confidence >= 60 && a.llmExtraction.confidence < 80
  ).length;
  const lowConfidence = analyses.filter(
    a => a.llmExtraction.confidence < 60
  ).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Smart Vendor Analysis Results
          </DialogTitle>
          <DialogDescription>
            Review AI-identified vendors and approve creation. High-confidence
            matches are pre-selected.
          </DialogDescription>
        </DialogHeader>

        {/* Statistics */}
        <div className="grid grid-cols-4 gap-4 py-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold">{analyses.length}</p>
                <p className="text-xs text-muted-foreground">Total Analyzed</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-[var(--success)]">
                  {highConfidence}
                </p>
                <p className="text-xs text-muted-foreground">High Confidence</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-[var(--warning)]">
                  {mediumConfidence}
                </p>
                <p className="text-xs text-muted-foreground">
                  Medium Confidence
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-destructive">
                  {lowConfidence}
                </p>
                <p className="text-xs text-muted-foreground">Low Confidence</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Vendor List */}
        <ScrollArea className="h-[400px] rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={selectedVendors.size === analyses.length}
                    onCheckedChange={toggleAll}
                  />
                </TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Product/Service</TableHead>
                <TableHead>MWST</TableHead>
                <TableHead>Confidence</TableHead>
                <TableHead>Transaction</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {analyses.map(analysis => (
                <TableRow key={analysis.transactionIndex}>
                  <TableCell>
                    <Checkbox
                      checked={selectedVendors.has(analysis.transactionIndex)}
                      onCheckedChange={() =>
                        toggleVendor(analysis.transactionIndex)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">
                          {analysis.llmExtraction.vendor.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Badge variant="outline" className="text-xs">
                          {analysis.llmExtraction.vendor.category}
                        </Badge>
                        {analysis.llmExtraction.vendor.swissVendor && (
                          <Badge
                            variant="outline"
                            className="text-xs bg-destructive/10"
                          >
                            🇨🇭 Swiss
                          </Badge>
                        )}
                        {analysis.enrichment && (
                          <Badge
                            variant="outline"
                            className="text-xs bg-[var(--info)]/10"
                          >
                            ✓ Verified
                          </Badge>
                        )}
                      </div>
                      {analysis.enrichment?.uid && (
                        <span className="text-xs text-muted-foreground font-mono">
                          {analysis.enrichment.uid}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <div className="flex flex-col gap-1">
                        <span className="text-sm">
                          {analysis.llmExtraction.product.name}
                        </span>
                        <Badge variant="secondary" className="text-xs w-fit">
                          {analysis.llmExtraction.product.type}
                        </Badge>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      {getMWSTBadge(analysis.llmExtraction.mwst.rate)}
                      <span className="text-xs text-muted-foreground">
                        {analysis.llmExtraction.mwst.category}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {getConfidenceBadge(analysis.llmExtraction.confidence)}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1 max-w-xs">
                      <span className="text-sm truncate">
                        {analysis.description}
                      </span>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>
                          {new Date(analysis.date).toLocaleDateString("de-CH")}
                        </span>
                        <span className="font-mono">
                          CHF {(analysis.amount / 100).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {analysis.llmExtraction.confidence < 80 && onCorrect && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onCorrect(analysis)}
                        className="h-8 px-2"
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Correct
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>

        <DialogFooter>
          <div className="flex items-center justify-between w-full">
            <p className="text-sm text-muted-foreground">
              {selectedVendors.size} of {analyses.length} vendors selected
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleApprove}
                disabled={selectedVendors.size === 0 || isProcessing}
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating Vendors...
                  </>
                ) : (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Create {selectedVendors.size} Vendors
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
